"use client";

import type { FC, Ref } from "react";
import {
  AskAITooltipContent,
  IconButton,
  type IconName,
  SummaryCard,
  type SummaryCardProps,
  type TableDataWithStyling,
  Tooltip,
  usePubSub,
  type UserPromptRecord,
  type ValueType,
} from "@huub-web/ui";

import { useTemplates } from "../../hooks/useTemplates";
import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components";

type SummaryListCardTemplatePropsType = Pick<
  SummaryCardProps,
  "title" | "value" | "columns" | "icon" | "body"
> & {
  data: {
    title: string;
    value: ValueType;
    helpPrompt?: UserPromptRecord;
    icon?: IconName;
  }[];
  helpPrompt?: UserPromptRecord;
  templates?: TemplateCollection;
  variant?: SummaryCardProps["listVariant"];
};

export interface SummaryListCardTemplateProps
  extends SummaryListCardTemplatePropsType {}

/**
 * A template for a summary list card.
 * @deprecated 💀
 */
export const SummaryListCardTemplate: FC<
  SummaryListCardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  title,
  value,
  body,
  icon,
  data,
  variant,
  helpPrompt,
  columns = "two",
  templates,
  ref,
}) => {
  const { lockPrompts } = useTemplates();
  const pubSub = usePubSub();

  const handleHelpPrompt = (prompt?: UserPromptRecord) => {
    if (prompt) {
      pubSub.publish("sendPrompt", { value: prompt });
    }
  };

  const parsedData: TableDataWithStyling = {
    headings: data?.map(({ title }) => ({ name: title })) ?? [],
    rows: [
      {
        values:
          data?.map(({ value, helpPrompt }) => ({
            value,
            trailing: helpPrompt && (
              <Tooltip
                value={<AskAITooltipContent prompt={helpPrompt} />}
                placement="bottom"
                disabled={lockPrompts}
              >
                <IconButton
                  icon="aiFilled"
                  variant="primary"
                  size="xxs"
                  rounded
                  disabled={lockPrompts}
                  onClick={() => handleHelpPrompt(helpPrompt)}
                />
              </Tooltip>
            ),
          })) ?? [],
      },
    ],
  };

  return (
    <SummaryCard
      ref={ref}
      title={title}
      body={body}
      value={value}
      icon={icon}
      data={parsedData}
      variant="list"
      listVariant={variant}
      columns={columns}
      headerTrailing={
        helpPrompt && (
          <Tooltip
            value={<AskAITooltipContent prompt={helpPrompt} />}
            disabled={lockPrompts}
            placement="bottom"
          >
            <IconButton
              icon="aiFilled"
              variant="primary"
              onClick={() => handleHelpPrompt(helpPrompt)}
              size="xxs"
              disabled={lockPrompts}
              rounded
            />
          </Tooltip>
        )
      }
    >
      {templates && <TemplatesParser templates={templates} level={1} />}
    </SummaryCard>
  );
};
